import { t } from '@lingui/core/macro';
import { analytics } from '@luminovo/data-stack';
import {
    buildHref,
    linkOptions,
    MenuItem,
    useHistoryState,
    useIsRouteActive,
    useLocationHref,
    useNavigate,
    useRouteMatches,
    useRouteName,
} from '@luminovo/design-system';
import { ArrowLeftRightIcon } from 'lucide-react';
import * as React from 'react';
import type { JSX } from 'react';

import { chooseNewNavigation, isNewNavigationChosen, newRouteOf, splatHrefAt } from '@/app/newNavigation';
import { canUseNewNavigation } from '@/shared/permissions/pagePermissions';
import { useSession } from '@/shared/permissions/session';

const newTree = linkOptions({ to: '/next' });

/**
 * The user menu's entry that turns the new navigation on and off in this browser, for those who may use it, in both
 * navigations. Under a new URL it is the way back, also where the browser never chose the new navigation, as when a
 * link led there. `onClose` closes the menu, which a page the switch doesn't move would keep open.
 */
export function NewNavigationSwitch({ onClose }: { onClose: () => void }): JSX.Element | null {
    const session = useSession();
    const [chosen, setChosen] = React.useState(isNewNavigationChosen);
    const switchNavigation = useSwitchNavigation();
    const isNew = useIsRouteActive(newTree) || chosen;

    if (!canUseNewNavigation(session)) {
        return null;
    }

    return (
        <MenuItem
            label={isNew ? t`Use old navigation` : t`Use new navigation`}
            startIcon={<ArrowLeftRightIcon />}
            onClick={() => {
                switchNavigation(!isNew);
                setChosen(!isNew);
                onClose();
            }}
        />
    );
}

/**
 * Saves this browser's choice and moves the page on screen to its URL in the navigation chosen, or, leaving a page
 * that has no old URL, to `/`. It navigates, never reloads, so the unsaved-changes guard asks before the page goes.
 */
function useSwitchNavigation(): (chosen: boolean) => void {
    const navigate = useNavigate();
    // A moved page has today's route as its name under its new URL too.
    const routeName = useRouteName();
    // The page on screen's, as its name is: `params: true` would take those of a page still loading.
    const params = useRouteMatches().at(-1)?.params;
    const isOnNewUrl = useIsRouteActive(newTree);
    // A page may start from it, such as a duplicated template's.
    const state = useHistoryState((current) => current);
    const href = useLocationHref();

    return (chosen) => {
        chooseNewNavigation(chosen);
        analytics.track('new_navigation_switched', { chosen });
        const moved = routeName ? newRouteOf(routeName) : undefined;
        // Straight to the page's other URL, as the reroute's redirect would skip the unsaved-changes guard; replaced, as
        // the reroute does, so Back doesn't land on the URL left. Only today's navbar switches on, under today's URL.
        if (chosen && moved) {
            navigate({ to: moved, params, search: true, hash: true, state: true, replace: true });
        } else if (!chosen && isOnNewUrl) {
            if (routeName && moved) {
                // By its URL, which takes no `state: true`: sent to its old route, `navigate` keeps a moved page under
                // the new one.
                navigate({
                    // A splat as typed (`splatHrefAt`), as the reroute moves it: by `params` the router decodes and
                    // encodes it again, so `/next/apps/a%2Fb$c` would become `/apps/a/b%24c`.
                    href:
                        splatHrefAt(moved, routeName, href) ??
                        buildHref({ to: routeName, params, search: true, hash: true }),
                    replace: true,
                    state,
                });
            } else {
                navigate({ to: '/' });
            }
        }
    };
}
